import { Icon } from '@iconify/react';
import {
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { Stack, Typography } from '../components/mui-system';
import {
  RESOURCE_PLATFORMS,
  type ResourcePlatform,
} from '../../../api/src/registry/registry-platform';
import type { RegistryCatalogItem } from '../../../api/src/registry/registry-catalog.types';

export const registryOwnershipLabels = {
  owned: 'Наш',
  third_party: 'Сторонний',
  unknown: 'Не установлена',
  conflict: 'Конфликт оснований',
};
export const registryManagementLabels = {
  managed: 'Управляем',
  unmanaged: 'Не управляем',
  unknown: 'Не установлено',
};
export function PlatformIcon({ platform }: { platform: ResourcePlatform }) {
  return (
    <Icon icon={RESOURCE_PLATFORMS[platform].icon} width={24} height={24} aria-hidden="true" />
  );
}
export function RegistryOwnershipChip({ value }: { value: RegistryCatalogItem['ownership'] }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={registryOwnershipLabels[value]}
      color={value === 'owned' ? 'success' : value === 'conflict' ? 'warning' : 'default'}
    />
  );
}
export function RegistryCatalogTable({
  items,
  resourcePath,
}: {
  items: RegistryCatalogItem[];
  resourcePath: (key: string) => string;
}) {
  return (
    <TableContainer>
      <Table aria-label="Ресурсы реестра" size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ресурс</TableCell>
            <TableCell>Принадлежность</TableCell>
            <TableCell>Управление</TableCell>
            <TableCell>Донорские публикации</TableCell>
            <TableCell>В поисковой выдаче</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row) => (
            <TableRow key={row.key} hover>
              <TableCell sx={{ minWidth: 260, maxWidth: 460, overflowWrap: 'anywhere', py: 2 }}>
                <Stack direction="row" alignItems="flex-start" gap={1.5}>
                  <Stack
                    sx={{ color: 'primary.main', bgcolor: 'action.hover', borderRadius: 2, p: 1 }}
                  >
                    <PlatformIcon platform={row.platform} />
                  </Stack>
                  <Stack gap={0.4}>
                    <Link
                      component={RouterLink}
                      to={resourcePath(row.key)}
                      sx={{ fontWeight: 600 }}
                    >
                      {row.title}
                    </Link>
                    {row.address !== row.title && (
                      <Typography variant="body2" color="text.secondary">
                        {row.address}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {RESOURCE_PLATFORMS[row.platform].label}
                      {row.kind === 'page'
                        ? ' · отдельная страница'
                        : row.kind === 'profile'
                          ? ' · канал / аккаунт'
                          : ''}
                    </Typography>
                    {row.roles.includes('competitor') && (
                      <Chip size="small" label="Конкурент" sx={{ alignSelf: 'flex-start' }} />
                    )}
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell>
                <RegistryOwnershipChip value={row.ownership} />
                {row.hasPageOwnership && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Есть отметки отдельных URL
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color={row.management === 'unknown' ? 'text.secondary' : 'text.primary'}
                >
                  {registryManagementLabels[row.management]}
                </Typography>
              </TableCell>
              <TableCell>
                {row.donorCount ? (
                  <Link component={RouterLink} to={resourcePath(row.key)}>
                    {row.donorCount} URL
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Нет известных
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {row.lastSeenAt ? (
                  <Stack>
                    <Typography variant="body2">
                      {row.engines
                        .map((engine) => (engine === 'google' ? 'Google' : 'Яндекс'))
                        .join(' · ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(row.lastSeenAt).toLocaleDateString('ru-RU')}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Нет наблюдений
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
